import dayjs from 'dayjs'
import { useNavigate } from 'react-router-dom'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { visuallyHidden } from '@mui/utils'
import { useQueryParam, StringParam } from 'use-query-params'
import Tab, { tabClasses } from '@mui/material/Tab'
import MaterialTabs, { tabsClasses } from '@mui/material/Tabs'
import Box, { boxClasses } from '@mui/material/Box'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Popover from '@mui/material/Popover'
import Stack from '@mui/material/Stack'
import { useTheme, useMediaQuery, Typography } from '@mui/material'
import CardBase from 'components/Card/CardBase/CardBase'
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { toSx } from 'utils/sx'
import ScreenShareIcon from '@mui/icons-material/ScreenShare'
import Dialog, { dialogClasses } from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import CloseIcon from '@mui/icons-material/Close'
import styled from '@emotion/styled'
import ButtonPrimary from 'components/ButtonPrimary/ButtonPrimary'
import ButtonSecondary from 'components/ButtonSecondary/ButtonSecondary'

const DEFAULT_PERIOD = 'week'

export const tabsStyles = (theme) => ({
  root: {
    backgroundColor: theme.vars.palette.background.tabs,
    borderRadius: '0.8rem',
    minHeight: 44,
    overflow: 'visible',
  },
  flexContainer: {
    position: 'relative',
    padding: '0 3px',
    zIndex: 1,
    overflow: 'visible',
  },
  indicator: {
    top: 2,
    bottom: 2,
    right: 3,
    height: 'auto',
    borderRadius: '8px',
    backgroundColor: theme.vars.palette.background.paper,
  },
  scroller: {
    paddingRight: '8px',
  },
})

export const tabItemStyles = (theme) => ({
  root: {
    minHeight: '4.8rem',
    opacity: 0.7,
    color: theme.vars.palette.text.primary,
    transitionProperty: 'color, opacity',
    transitionDuration: `${theme.transitions.duration.standard}ms}`,
    ...theme.typography.button,
    '&:hover': {
      opacity: 1,
    },
    [`&.${tabClasses.selected}`]: {
      color: `${theme.vars.palette.text.primary} !important`,
      opacity: 1,
    },
  },
})

const BoxStyles = (theme) => ({
  root: {
    display: 'flex',
    backgroundColor: theme.vars.palette.background.tabs,
    borderRadius: '0.8rem',
    padding: '2px',
  },
})

const StyledDialog = styled(Dialog)(() => ({
  [`& .${dialogClasses.root}`]: {
    padding: '2rem 1.6rem 1.6rem',
  },
  [`& .${dialogClasses.paper}`]: {
    margin: 0,
  },
}))

const parseInitialRangeValue = (query = '') => {
  const parsed = query
    .split('_')
    .map((date) => (dayjs(date).isValid() ? dayjs(date) : dayjs()))

  return parsed.length > 1 ? parsed : [parsed[0], parsed[0]]
}

function Tabs({ items = [], hasNewsletterButton }) {
  const [periodQuery, setPeriodQuery] = useQueryParam('period', StringParam)

  const initialIsRangeFilter = periodQuery && !items.some(item => item.value === periodQuery)
  const initialTabsValue = initialIsRangeFilter ? 'range' : periodQuery || items[0]?.value

  const [isRangeFilter, setIsRangeFilter] = useState(initialIsRangeFilter)
  const [tabsValue, setTabsValue] = useState(initialTabsValue)
  const [isRangeFilterOpen, setIsRangeFilterOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState(null)

  const initialDates = periodQuery && initialIsRangeFilter
    ? parseInitialRangeValue(periodQuery)
    : [dayjs(), dayjs()]

  const [toDateValue, setToDateValue] = useState(initialDates[0])
  const [fromDateValue, setFromDateValue] = useState(initialDates[1])

  const ref = useRef()

  const navigate = useNavigate()
  const tabItemSx = toSx(tabItemStyles, tabClasses)
  const boxSx = toSx(BoxStyles, boxClasses)
  const open = Boolean(anchorEl)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'))
  const [openModal, setOpenModal] = useState(false)

  const setTabLabel = useCallback(() => {
    if (isRangeFilterOpen) return

    ref.current = `${fromDateValue.format('DD.MM.YYYY')} — ${toDateValue.format(
      'DD.MM.YYYY'
    )}`
  }, [fromDateValue, toDateValue, isRangeFilterOpen])

  useEffect(() => {
    const isRangeTabActive = tabsValue === 'range'
    setIsRangeFilter(isRangeTabActive)

    if (isRangeTabActive) {
      setTabLabel()
    }

    if (isRangeTabActive && !isRangeFilterOpen) {
      const newRange = `${fromDateValue.toISOString()}_${toDateValue.toISOString()}`
      if (periodQuery !== newRange) {
        setPeriodQuery(newRange)
      }
      return
    }

    if (!isRangeTabActive && !openModal) {
      if (periodQuery !== tabsValue) {
        setPeriodQuery(tabsValue)
      }
    }
  }, [
    openModal,
    isRangeFilter,
    isRangeFilterOpen,
    fromDateValue,
    toDateValue,
    tabsValue,
    setPeriodQuery,
    setTabLabel,
    periodQuery
  ])

  const handleClick = (e) => {
    setAnchorEl(e.currentTarget)
    setIsRangeFilterOpen(true)
  }

  const handleCreateNewsletter = () => {
    // const params = new URLSearchParams(window.location.search)
    // navigate(`/newsletter?${params.toString()}`)
    navigate(`/newsletter`)
  }

  const handlePopoverClose = () => {
    if (!fromDateValue && !toDateValue) {
      setFromDateValue(dayjs())
      setToDateValue(dayjs())
    }

    if (!fromDateValue && toDateValue) {
      setFromDateValue(toDateValue)
    }

    if (fromDateValue && !toDateValue) {
      setToDateValue(fromDateValue)
    }

    setIsRangeFilterOpen(false)
    setAnchorEl(null)
  }

  const handleApplyClick = () => {
    handlePopoverClose()
    setIsRangeFilterOpen(false)
    setTabLabel()
    setTabsValue('range')
  }

  const handleTabChange = (e, value) => {
    setTabsValue(value)
  }

  const handleToDateChange = (value) => setToDateValue(value)
  const handleFromDateChange = (value) => setFromDateValue(value)

  const handleOpenModal = () => {
    setIsRangeFilterOpen(true)
    setOpenModal(true)
  }

  const handleCloseModal = () => {
    setIsRangeFilterOpen(false)
    setOpenModal(false)
  }

  const handleModalApply = () => {
    handleCloseModal()
  }

  const handleModalReset = () => {
    setTabsValue(DEFAULT_PERIOD)
  }

  return (
    <>
      {!isMobile ? (
        <CardBase>
          <Stack
            direction="row"
            spacing={2}
            sx={{
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            {items.length ? (
              <Box sx={boxSx}>
                <MaterialTabs
                  value={tabsValue}
                  onChange={(e, value) => handleTabChange(e, value)}
                  sx={[toSx(tabsStyles, tabsClasses)]}
                  variant={ref.current ? 'scrollable' : 'standard'}
                >
                  {items.map(({ label, value }) => (
                    <Tab
                      variant="button"
                      key={value}
                      disableRipple
                      label={label}
                      value={value}
                      sx={tabItemSx}
                    />
                  ))}
                  (
                  <Tab
                    disableRipple
                    label={ref.current}
                    value="range"
                    sx={{
                      ...tabItemSx,
                      ...(ref.current ? {} : visuallyHidden),
                    }}
                  />
                  )
                </MaterialTabs>

                <IconButton
                  sx={{ alignSelf: 'center', mx: '1rem', opacity: 0.5 }}
                  onClick={handleClick}
                  aria-describedby="calendar-menu"
                  aria-label="Календарь фильтра"
                  size="small"
                  aria-controls={open ? 'calendar-menu' : undefined}
                  aria-haspopup="true"
                  aria-expanded={open ? 'true' : undefined}
                >
                  <CalendarMonthRoundedIcon />
                </IconButton>

                <Popover
                  anchorEl={anchorEl}
                  id="calendar-menu"
                  open={open}
                  onClose={handlePopoverClose}
                  transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                  anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                >
                  <Stack
                    direction="row"
                    spacing={2}
                    sx={{
                      justifyContent: 'center',
                      alignItems: 'center',
                      padding: '1.6rem',
                    }}
                  >
                    <DatePicker
                      onChange={handleFromDateChange}
                      label="От"
                      value={fromDateValue}
                      format="DD/MM/YYYY"
                      disableFuture={true}
                      maxDate={toDateValue}
                      slotProps={{
                        field: { clearable: true },
                        textField: { size: 'small', sx: { width: '20rem' } },
                        toolbar: {
                          hidden: true,
                        },
                      }}
                    />
                    <DatePicker
                      onChange={handleToDateChange}
                      label="До"
                      format="DD/MM/YYYY"
                      value={toDateValue}
                      disableFuture={true}
                      minDate={fromDateValue}
                      slotProps={{
                        field: { clearable: true },
                        textField: { size: 'small', sx: { width: '20rem' } },
                        toolbar: {
                          hidden: true,
                        },
                      }}
                    />
                    <ButtonPrimary
                      size="small"
                      disabled={!toDateValue || !fromDateValue}
                      onClick={handleApplyClick}
                    >
                      Применить
                    </ButtonPrimary>
                  </Stack>
                </Popover>
              </Box>
            ) : null}

            <Stack direction="row" spacing={2} sx={{ marginLeft: 'auto' }}>
              {hasNewsletterButton ? (
                <ButtonSecondary color="secondary" onClick={handleCreateNewsletter}>
                  Сформировать рассылку
                </ButtonSecondary>
              ) : null}
            </Stack>
          </Stack>
        </CardBase>
      ) : (
        <>
          <Stack
            direction="row"
            spacing={1.6}
            sx={{
              justifyContent: 'center',
              padding: '1.6rem',
              borderRadius: '1.6rem',
              backgroundColor: 'background.tertiary',
            }}
          >
            <Stack
              spacing={0.8}
              sx={{ alignItems: 'center', minWidth: '9.2rem' }}
            >
              <Button variant="contained" fullWidth onClick={handleOpenModal}>
                <CalendarMonthRoundedIcon />
              </Button>
              <Typography variant="tabCaption" sx={{ textAlign: 'center' }}>
                Выбрать период
              </Typography>
            </Stack>

            {hasNewsletterButton ? (
              <Stack
                spacing={0.8}
                sx={{ alignItems: 'center', minWidth: '9.2rem' }}
              >
                <Button variant="contained" fullWidth onClick={handleCreateNewsletter}>
                  <ScreenShareIcon />
                </Button>
                <Typography variant="tabCaption" sx={{ textAlign: 'center' }}>
                  Создать рассылку
                </Typography>
              </Stack>
            ) : null}
          </Stack>
          <StyledDialog fullScreen open={openModal} onClose={handleCloseModal}>
            <DialogTitle
              sx={{ padding: '1.6rem 1.6rem 2rem' }}
              id="customized-dialog-title"
            >
              <Typography variant="logo">Выбрать период</Typography>
            </DialogTitle>
            <IconButton
              aria-label="close"
              onClick={handleCloseModal}
              sx={(theme) => ({
                position: 'absolute',
                right: 8,
                top: 14,
                color: theme.vars.palette.text.primary,
              })}
            >
              <CloseIcon />
            </IconButton>
            <DialogContent dividers>
              <Stack spacing={3.2}>
                <MaterialTabs
                  value={tabsValue}
                  onChange={(e, value) => handleTabChange(e, value)}
                  sx={[toSx(tabsStyles, tabsClasses)]}
                  variant="scrollable"
                >
                  {items.map(({ label, value }) => (
                    <Tab
                      variant="button"
                      key={value}
                      disableRipple
                      label={label}
                      value={value}
                      sx={tabItemSx}
                    />
                  ))}
                  <Tab
                    disableRipple
                    variant="button"
                    label="Заданный"
                    value="range"
                    sx={tabItemSx}
                  />
                </MaterialTabs>
                {tabsValue === 'range' ? (
                  <Stack spacing={2}>
                    <DatePicker
                      onChange={handleFromDateChange}
                      label="От"
                      value={fromDateValue}
                      format="DD/MM/YYYY"
                      disableFuture={true}
                      maxDate={toDateValue}
                      slotProps={{
                        field: { clearable: true },
                        textField: { size: 'large', sx: { width: '100%' } },
                        toolbar: {
                          hidden: true,
                        },
                      }}
                    />
                    <DatePicker
                      onChange={handleToDateChange}
                      label="До"
                      format="DD/MM/YYYY"
                      value={toDateValue}
                      disableFuture={true}
                      minDate={fromDateValue}
                      slotProps={{
                        field: { clearable: true },
                        textField: { size: 'large', sx: { width: '100%' } },
                        toolbar: {
                          hidden: true,
                        },
                      }}
                    />
                  </Stack>
                ) : null}
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button
                variant="contained"
                autoFocus
                fullWidth
                disabled={!toDateValue || !fromDateValue}
                onClick={handleModalApply}
              >
                Применить
              </Button>
              <Button
                variant="outlined"
                autoFocus
                fullWidth
                onClick={handleModalReset}
              >
                Сбросить
              </Button>
            </DialogActions>
          </StyledDialog>
        </>
      )}
    </>
  )
}

export default Tabs
